import { I18n } from "@assets/strings";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { Organization } from "@domain/organization/organization";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { createToast } from "@ui/toasts/utils";
import { CommunityIcon, RepostIcon } from "@ui/components/icons";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { IconWrapper, SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";
import { OrganizationEditionModal } from "./organizationEditionModal";
import { TransferOwnershipModal } from "./transferOwnershipModal";

interface OrganizationMenuProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
	blur?: boolean;
}
export const OrganizationMenu: React.FC<OrganizationMenuProps> = ({ organization, blur, ...htmlProps }) => {
	const { createModal } = useModalTools();
	const alias = useOrganizationAlias();

	const { organizationService } = useServices();
	const askDeleteConfirmation = useConfirmation(
		I18n.t("organization.delete.title"),
		I18n.t("delete"),
		I18n.t("organization.delete.infos")
	);
	const history = useHistory();

	return (
		<KebabMenu blur={blur} closeOnSelect onClick={(e) => e.stopPropagation()} {...htmlProps} authOnly>
			<SimpleMenuContent
				onEdit={
					alias
						? () => createModal((close) => <OrganizationEditionModal organizationId={organization.id} close={close} />)
						: undefined
				}
				onDelete={
					alias && organization.userRoles.includes("SUPER_ADMINISTRATOR")
						? () =>
								askDeleteConfirmation(async () => {
									await organizationService.delete(organization.id);
									createToast(I18n.t("success"), I18n.t("organization.toast.delete.message"), "success");
									history.push(Path.Root);
								})
						: undefined
				}
				reportInfos={
					alias
						? undefined
						: {
								contentId: organization.id,
								contentType: "ORGANIZATION",
							}
				}
			>
				<MenuRow
					onClick={() =>
						createModal((close) => (
							<EditReferencePostModal close={close} shared={{ type: "SHARE_ORGANIZATION", reference: organization }} />
						))
					}
				>
					<IconWrapper>
						<RepostIcon />
					</IconWrapper>
					<span>{I18n.t("share")}</span>
				</MenuRow>
				{alias && organization.userRoles.includes("SUPER_ADMINISTRATOR") && (
					<MenuRow
						onClick={() =>
							createModal((close) => <TransferOwnershipModal organizationId={organization.id} close={close} />)
						}
					>
						<IconWrapper>
							<CommunityIcon />
						</IconWrapper>
						<span>{I18n.t("organization.transfer_ownership.button")}</span>
					</MenuRow>
				)}
			</SimpleMenuContent>
		</KebabMenu>
	);
};
